import { useLayoutEffect, useMemo, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { createStore } from 'tinybase';
import { useCreateStore, useTable } from 'tinybase/ui-react';

import {
  useGetSearchPerformancePagesBySourceQuery,
  type SearchPerformancePagesQueryArgs,
  type SearchPerformancePagesResponse,
  type SearchPerformancePagesSourceType,
} from '../api/searchPerformanceApi';
import { toRequestLoad, type RequestLoad } from '../SearchPerformanceRequestLoad';
import { type StatusCodeClass } from '../StatusCodeClass';
import { inspectedWithinDays, useUrlInspectionAccess } from '../UrlInspection';
import { PAGES_PAGE_SIZE, pageOffset } from './PagesPaging';
import { SORT_FIELD_API_SORT_BY, SORT_FIELD_SOURCE, otherSource, type PagesSort } from './PagesSort';
import { PAGES_TABLE, loadSourceRows, readJoinedRow, type PagesJoinedRow } from './PagesStore';
import { type PagesTabKey } from './PagesTabs';

export type PagesRowsQuery = {
  /** `undefined` until the reporting property resolves; nothing is fetched before that. */
  property: string | undefined;
  dateStart: string;
  dateEnd: string;
  tab: PagesTabKey;
  sort: PagesSort;
  /** 1-based. */
  page: number;
  urlContains?: string;
  statusFilter?: StatusCodeClass[];
};

export type PagesLoadCycle = {
  load: RequestLoad;
  columns: RequestLoad | undefined;
  sourceType: SearchPerformancePagesSourceType;
  hasData: boolean;
};

export type PagesRowsResult = {
  /** Rows of the current page in the owner's order, both sources' columns joined on pageId. */
  rows: PagesJoinedRow[];
  /** The tab's unpaged total, from the owner fetch — never a client count. */
  total: number;
  /** Rows resolved at least once for this property + tab; the table can render. */
  hasRows: boolean;
  isUninitialized: boolean;
  isFetching: boolean;
  /** The owner fetch failed: there are no rows to show. */
  isError: boolean;
  /** Only the decoration fetch failed: the rows stand, the other side's columns read `—`. */
  isDecorationError: boolean;
  refetch: () => void;
  loadCycle: PagesLoadCycle;
};

/**
 * One table page = two fetches. The owner fetch lists the tab from the view
 * that owns the active sort (server-sorted, server-paged, exact total). The
 * decoration fetch asks the other view for exactly those pageIds and only
 * fills the other side's columns. Both land in one TinyBase table keyed by
 * pageId; the rows read back in the owner's order.
 */
export function usePagesRows(query: PagesRowsQuery): PagesRowsResult {
  const store = useCreateStore(createStore);
  const hasUrlInspectionAccess = useUrlInspectionAccess();

  const ownerSource = SORT_FIELD_SOURCE[query.sort.field];
  const ownerArgs: SearchPerformancePagesQueryArgs | typeof skipToken = query.property
    ? {
        dateStart: query.dateStart,
        dateEnd: query.dateEnd,
        property: query.property,
        sourceType: ownerSource,
        tab: query.tab,
        sortBy: SORT_FIELD_API_SORT_BY[query.sort.field],
        sortOrder: query.sort.order,
        urlContains: query.urlContains,
        lastStatusCodeClasses: query.statusFilter,
        limit: PAGES_PAGE_SIZE,
        offset: pageOffset(query.page),
        inspectedWithinDays: inspectedWithinDays(ownerSource, hasUrlInspectionAccess),
      }
    : skipToken;
  const owner = useGetSearchPerformancePagesBySourceQuery(ownerArgs);

  // `data` is the last result for any arg; `currentData` is this arg's. Keep
  // `data` across sort/page/filter refetches so the table overlays instead of
  // skeletonizing — but never across a property or tab switch, where the stale
  // rows belong to a different population.
  const scopeKey = `${query.property}|${query.tab}`;
  const lastFulfilledScope = useRef<string | undefined>(undefined);
  if (owner.currentData !== undefined) {
    lastFulfilledScope.current = scopeKey;
  }
  const resolvedOwner: SearchPerformancePagesResponse | undefined =
    owner.currentData ?? (lastFulfilledScope.current === scopeKey ? owner.data : undefined);

  // Visibility lists a page once per PRERENDER companion (CEN-290), so a
  // listing can repeat a pageId; the row key and the decoration ids cannot.
  const ownerIds = useMemo(
    () => Array.from(new Set(resolvedOwner?.rows.map((row) => row.pageId) ?? [])),
    [resolvedOwner]
  );

  // A sort can move the owner to the other source while the kept listing is
  // still the previous owner's (`owner.data`). Its rows say which source
  // listed them; everything below follows that, not the source in flight.
  const listingSource = resolvedOwner?.rows[0]?.sourceType ?? ownerSource;
  const decorationArgs: SearchPerformancePagesQueryArgs | typeof skipToken =
    query.property && ownerIds.length > 0
      ? {
          dateStart: query.dateStart,
          dateEnd: query.dateEnd,
          property: query.property,
          sourceType: otherSource(listingSource),
          pageIds: ownerIds,
          inspectedWithinDays: inspectedWithinDays(otherSource(listingSource), hasUrlInspectionAccess),
        }
      : skipToken;
  const decoration = useGetSearchPerformancePagesBySourceQuery(decorationArgs);
  const isDecorationRequested = decorationArgs !== skipToken;

  const isDecorationError = !owner.isError && decoration.isError;

  // Layout effects, not passive ones: a fulfilled fetch changes `ownerIds` in
  // this render but the store only learns the rows in the effect, so a passive
  // effect would let React paint the new ids against the old store — a frame
  // of blank rows — before the store listener re-renders. Before-paint writes
  // keep that frame off screen.
  useLayoutEffect(() => {
    if (resolvedOwner) loadSourceRows(store, listingSource, resolvedOwner.rows);
  }, [store, listingSource, resolvedOwner]);

  // A visibility predating CEN-291 strips `page_ids` and answers with its
  // unfiltered view: only rows that were asked for may decorate the page.
  const decorationRows = decoration.currentData?.rows;
  useLayoutEffect(() => {
    if (!decorationRows) return;
    const requested = new Set(ownerIds);
    loadSourceRows(
      store,
      otherSource(listingSource),
      decorationRows.filter((row) => requested.has(row.pageId))
    );
  }, [store, listingSource, decorationRows, ownerIds]);

  useTable(PAGES_TABLE, store);
  const rows = ownerIds.map((pageId) => readJoinedRow(store, pageId));

  return {
    rows,
    total: resolvedOwner?.total ?? 0,
    hasRows: resolvedOwner !== undefined,
    isUninitialized: owner.isUninitialized,
    isFetching: owner.isFetching || decoration.isFetching,
    isError: owner.isError,
    isDecorationError,
    refetch: () => {
      owner.refetch();
      if (isDecorationRequested) decoration.refetch();
    },
    loadCycle: {
      load: toRequestLoad(owner),
      columns: isDecorationRequested && !owner.isError ? toRequestLoad(decoration) : undefined,
      sourceType: ownerSource,
      hasData: (resolvedOwner?.total ?? 0) > 0,
    },
  };
}
